import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertCircle, Search, Loader2 } from "lucide-react";
import addressService from "@/services/addressService";
import { showError } from "@/store/errorStore";

const EMPTY_FORM = {
  cep: "",
  logradouro: "",
  number: "",
  complemento: "",
  bairro: "",
  localidade: "",
  estado: "",
  uf: "",
  principal: false,
};

const FieldError = ({ message }) =>
  message ? (
    <div className="flex items-center gap-1 text-red-500 text-sm">
      <AlertCircle className="w-4 h-4" />
      <span>{message}</span>
    </div>
  ) : null;

/**
 * Modal para editar (ou criar) endereço.
 * Envia o AddressDTO da API (cep, logradouro, bairro, localidade, uf...).
 */
export default function AddressFormModal({
  open,
  onOpenChange,
  address,
  onSave,
  loading,
}) {
  const isEditing = !!address;

  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [searchingCEP, setSearchingCEP] = useState(false);

  useEffect(() => {
    if (address) {
      setFormData({
        cep: addressService.formatCEP(address.cep || ""),
        logradouro: address.logradouro || "",
        number: address.number || "",
        complemento: address.complemento || "",
        bairro: address.bairro || "",
        localidade: address.localidade || "",
        estado: address.estado || "",
        uf: address.uf || "",
        principal: !!address.principal,
      });
    } else {
      setFormData(EMPTY_FORM);
    }
    setErrors({});
  }, [address, open]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleCEPChange = (value) => {
    handleChange("cep", value.replace(/[^\d-]/g, ""));
  };

  const handleSearchCEP = async () => {
    const cep = formData.cep.replace(/\D/g, "");
    if (cep.length !== 8) {
      showError("CEP deve ter 8 dígitos", "CEP Inválido");
      return;
    }

    setSearchingCEP(true);
    try {
      const data = await addressService.searchByCEP(cep);
      setFormData((prev) => ({
        ...prev,
        logradouro: data.logradouro || prev.logradouro,
        bairro: data.bairro || prev.bairro,
        localidade: data.localidade || prev.localidade,
        estado: data.estado || prev.estado,
        uf: data.uf || prev.uf,
        cep: addressService.formatCEP(cep),
      }));
    } catch (error) {
      showError(error.message || "Erro ao buscar CEP", "Erro na Busca de CEP");
    } finally {
      setSearchingCEP(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const validation = addressService.validateAddressData(formData);
    setErrors(validation.errors);
    if (!validation.valid) return;

    onSave(addressService.buildPayload(formData));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Editar Endereço" : "Novo Endereço"}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? "Atualize as informações do endereço."
              : "Preencha os dados para criar um novo endereço."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* CEP with Search */}
          <div className="space-y-2">
            <Label htmlFor="cep">
              CEP <span className="text-red-500">*</span>
            </Label>
            <div className="flex gap-2">
              <Input
                id="cep"
                placeholder="12345-678"
                value={formData.cep}
                onChange={(e) => handleCEPChange(e.target.value)}
                className={errors.cep ? "border-red-500" : ""}
                disabled={loading}
                maxLength={9}
              />
              <Button
                type="button"
                variant="outline"
                onClick={handleSearchCEP}
                disabled={loading || searchingCEP}
                title="Buscar CEP"
              >
                {searchingCEP ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Search className="w-4 h-4" />
                )}
              </Button>
            </div>
            <FieldError message={errors.cep} />
            <p className="text-xs text-gray-500">
              Clique na lupa para buscar automaticamente
            </p>
          </div>

          {/* Logradouro */}
          <div className="space-y-2">
            <Label htmlFor="logradouro">
              Rua <span className="text-red-500">*</span>
            </Label>
            <Input
              id="logradouro"
              placeholder="Rua das Flores"
              value={formData.logradouro}
              onChange={(e) => handleChange("logradouro", e.target.value)}
              className={errors.logradouro ? "border-red-500" : ""}
              disabled={loading}
            />
            <FieldError message={errors.logradouro} />
          </div>

          {/* Number and Complement */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="number">
                Número <span className="text-red-500">*</span>
              </Label>
              <Input
                id="number"
                placeholder="123"
                value={formData.number}
                onChange={(e) => handleChange("number", e.target.value)}
                className={errors.number ? "border-red-500" : ""}
                disabled={loading}
              />
              <FieldError message={errors.number} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="complemento">Complemento</Label>
              <Input
                id="complemento"
                placeholder="Apto 101"
                value={formData.complemento}
                onChange={(e) => handleChange("complemento", e.target.value)}
                disabled={loading}
              />
            </div>
          </div>

          {/* Bairro */}
          <div className="space-y-2">
            <Label htmlFor="bairro">
              Bairro <span className="text-red-500">*</span>
            </Label>
            <Input
              id="bairro"
              placeholder="Centro"
              value={formData.bairro}
              onChange={(e) => handleChange("bairro", e.target.value)}
              className={errors.bairro ? "border-red-500" : ""}
              disabled={loading}
            />
            <FieldError message={errors.bairro} />
          </div>

          {/* Cidade e UF */}
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2 space-y-2">
              <Label htmlFor="localidade">
                Cidade <span className="text-red-500">*</span>
              </Label>
              <Input
                id="localidade"
                placeholder="São Paulo"
                value={formData.localidade}
                onChange={(e) => handleChange("localidade", e.target.value)}
                className={errors.localidade ? "border-red-500" : ""}
                disabled={loading}
              />
              <FieldError message={errors.localidade} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="uf">
                UF <span className="text-red-500">*</span>
              </Label>
              <Input
                id="uf"
                placeholder="SP"
                value={formData.uf}
                onChange={(e) => handleChange("uf", e.target.value.toUpperCase())}
                className={errors.uf ? "border-red-500" : ""}
                disabled={loading}
                maxLength={2}
              />
              <FieldError message={errors.uf} />
            </div>
          </div>

          {/* Principal */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="principal"
              checked={formData.principal}
              onCheckedChange={(checked) => handleChange("principal", !!checked)}
              disabled={loading}
            />
            <Label
              htmlFor="principal"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Definir como endereço principal
            </Label>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-[#1b5e20] hover:bg-[#144919] text-white"
            >
              {loading ? "Salvando..." : isEditing ? "Atualizar" : "Criar"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
